import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const PublicHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="/assets/logo.png" 
              alt="Prestige AI" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold text-cyan-600 hidden sm:inline">Prestige AI</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium ${
                isActive('/') 
                  ? 'text-cyan-600 border-b-2 border-cyan-500' 
                  : 'text-gray-600 hover:text-cyan-500'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 text-sm font-medium ${
                isActive('/about') 
                  ? 'text-cyan-600 border-b-2 border-cyan-500' 
                  : 'text-gray-600 hover:text-cyan-500'
              }`}
            >
              About Us
            </Link>
            <Link
              to="/solutions"
              className={`px-3 py-2 text-sm font-medium ${
                isActive('/solutions') 
                  ? 'text-cyan-600 border-b-2 border-cyan-500' 
                  : 'text-gray-600 hover:text-cyan-500'
              }`}
            >
              Solutions & Pricing
            </Link>
            <Link
              to="/confidentiality"
              className={`px-3 py-2 text-sm font-medium ${
                isActive('/confidentiality') 
                  ? 'text-cyan-600 border-b-2 border-cyan-500' 
                  : 'text-gray-600 hover:text-cyan-500'
              }`}
            >
              Confidentiality & Security
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-cyan-600 hover:text-cyan-700"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-lg shadow-sm"
            >
              Sign Up
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500"
            >
              <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
              {isMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive('/') 
                  ? 'text-cyan-600 bg-cyan-50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-cyan-500'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive('/about') 
                  ? 'text-cyan-600 bg-cyan-50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-cyan-500'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </Link>
            <Link
              to="/solutions"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive('/solutions') 
                  ? 'text-cyan-600 bg-cyan-50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-cyan-500'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Solutions & Pricing
            </Link>
            <Link
              to="/confidentiality"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive('/confidentiality') 
                  ? 'text-cyan-600 bg-cyan-50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-cyan-500'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Confidentiality & Security
            </Link>
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-5">
              <div className="flex-shrink-0">
                <img 
                  src="/assets/logo.png" 
                  alt="Prestige AI" 
                  className="h-10 w-10 object-contain"
                />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">Prestige AI</div>
                <div className="text-sm font-medium text-gray-500">AI-Powered Financial Solutions</div>
              </div>
            </div>
            <div className="mt-3 px-2 space-y-1">
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-cyan-500"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="block px-3 py-2 rounded-md text-base font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;